'use client';

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

/**
 * Modal — Hauraki-styled Radix Dialog.
 *
 * Sizes:
 *   S  — 400px  (confirmations, single-action prompts)
 *   M  — 560px  (default; typical forms)
 *   L  — 720px  (rich forms, editors)
 *   XL — 960px  (multi-column, data-heavy)
 *
 * `showClose` — render the [x] close affordance in the top-right (RTL: top-
 * left). Defaults to true; set false for confirm-only prompts.
 *
 * Composition mirrors Radix Dialog. Use `Modal` as the root; put focusable
 * open trigger in `ModalTrigger`.
 */

export const Modal = DialogPrimitive.Root;
export const ModalTrigger = DialogPrimitive.Trigger;
export const ModalClose = DialogPrimitive.Close;
export const ModalPortal = DialogPrimitive.Portal;

export const ModalOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function ModalOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="modal-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-ink-primary/50 backdrop-blur-sm",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
});
ModalOverlay.displayName = "ModalOverlay";

const contentVariants = cva(
  [
    "fixed left-[50%] top-[50%] z-50 grid w-full",
    "translate-x-[-50%] translate-y-[-50%]",
    "gap-space-16 p-space-24",
    "bg-chrome-surface text-ink-primary",
    "border border-chrome-border rounded-modal shadow-xl",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
    "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
    // Safety on small viewports.
    "max-h-[90vh] overflow-y-auto",
  ].join(" "),
  {
    variants: {
      size: {
        S: "max-w-[400px]",
        M: "max-w-[560px]",
        L: "max-w-[720px]",
        XL: "max-w-[960px]",
      },
    },
    defaultVariants: { size: "M" },
  },
);

export interface ModalContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof contentVariants> {
  /** Render the trailing close icon in the corner. Default: true. */
  showClose?: boolean;
  /** Custom class for the overlay. */
  overlayClassName?: string;
}

export const ModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ModalContentProps
>(function ModalContent(
  {
    className,
    children,
    size,
    showClose = true,
    overlayClassName,
    ...props
  },
  ref,
) {
  return (
    <ModalPortal>
      <ModalOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="modal-content"
        data-size={size ?? "M"}
        className={cn(contentVariants({ size, className }))}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close
            aria-label="Close"
            className={cn(
              "absolute end-space-16 top-space-16 rounded-chip",
              "text-ink-muted hover:text-ink-primary",
              "outline-none focus-visible:ring-2 focus-visible:ring-accent-violet",
              "transition-colors duration-micro ease-standard",
            )}
          >
            <X className="size-4" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </ModalPortal>
  );
});
ModalContent.displayName = "ModalContent";

export const ModalHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ModalHeader({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="modal-header"
      className={cn("flex flex-col gap-space-4 text-start", className)}
      {...props}
    />
  );
});
ModalHeader.displayName = "ModalHeader";

export const ModalTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function ModalTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      data-slot="modal-title"
      className={cn("text-heading-22 text-ink-primary", className)}
      {...props}
    />
  );
});
ModalTitle.displayName = "ModalTitle";

export const ModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function ModalDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      data-slot="modal-description"
      className={cn("text-body-14 text-ink-muted", className)}
      {...props}
    />
  );
});
ModalDescription.displayName = "ModalDescription";

export const ModalFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ModalFooter({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="modal-footer"
      className={cn(
        "flex flex-col-reverse gap-space-8 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
});
ModalFooter.displayName = "ModalFooter";

export { contentVariants as modalContentVariants };
